import { AloftPrice } from '@aloft/toolkit-shared';
import {
  Block,
  Flex,
  Icon,
  InfoIcon,
  SimpleDollarInput,
  Typography,
  colors,
  theme,
} from '@aloft/toolkit-sui';

import { ReactNode } from 'react';
import { Controller } from 'react-hook-form';

import { FormTextarea } from '../../../components/FormTextarea/FormTextarea.tsx';
import { ValidationError, ValidationErrorType } from '../../../hooks/validation/types.ts';
import { RequiredFieldAnchor } from '../../alerts/RequiredFieldAnchor.tsx';
import { ReconciliationFormReturn } from '../types.ts';

const DiscrepancyWarningIcon = () => (
  <Flex
    width="28px"
    height="28px"
    borderRadius="50%"
    alignItems="center"
    justifyContent="center"
    backgroundColor="statusWarning"
    flexShrink={0}
  >
    <Typography
      color="textOffset"
      __dangerDoNotUseSx={{
        fontSize: '22px',
        lineHeight: '1',
        fontWeight: 700,
      }}
    >
      !
    </Typography>
  </Flex>
);

const formatValue = (value?: AloftPrice) => (value ? value.toString(true) : 'N/A');
const discrepancyDirection = (delta?: AloftPrice) => {
  if (!delta) return '';
  if (delta.toDollarLike() > 0) return 'increase';
  if (delta.toDollarLike() < 0) return 'decrease';
  return '';
};

const discrepancyDifference = (delta?: AloftPrice) => {
  if (!delta) return '';
  return AloftPrice.fromDollarLike(Math.abs(delta.toDollarLike())).toString(true);
};

export const OpinionsOfValueSection = ({
  active,
  form,
  isArvOrder,
  isMockOrder,
  includeTotalCostToCure,
  priorDesktopAiv,
  priorDesktopArv,
  watchedOpinionOfMarketValuePrice,
  watchedOpinionOfAfterRepairValuePrice,
  watchedTotalRenovationBudgetPrice,
  aivDelta,
  arvDelta,
  hasAivDiscrepancy,
  hasArvDiscrepancy,
  isHybridDesktopComparison,
  renovationBudgetWarningMessage,
  opinionOfMarketValueRangeWarningMessage,
  opinionOfAsIsValueRangeWarningMessage,
  opinionOfAfterRepairValueRangeWarningMessage,
  showIndicatedCostWarningGeneral,
  showIndicatedCostWarningAiv,
  showIndicatedCostWarningArv,
  shouldShowFeasibilityCommentary,
  roiDisplay,
  roiDisplayColor,
  totalCostToCureError,
  totalCostToCureCommentaryError,
  opinionOfMarketValueError,
  opinionOfAfterRepairValueError,
  totalRenovationBudgetError,
  feasibilityCommentaryError,
  aivDiscrepancyCommentaryError,
  arvDiscrepancyCommentaryError,
  renderChartOrSummary,
  handleChange,
  setSectionRef,
}: {
  active: boolean;
  form: ReconciliationFormReturn;
  isArvOrder: boolean;
  isMockOrder: boolean;
  includeTotalCostToCure?: boolean;
  priorDesktopAiv?: AloftPrice;
  priorDesktopArv?: AloftPrice;
  watchedOpinionOfMarketValuePrice?: AloftPrice;
  watchedOpinionOfAfterRepairValuePrice?: AloftPrice;
  watchedTotalRenovationBudgetPrice?: AloftPrice;
  aivDelta?: AloftPrice;
  arvDelta?: AloftPrice;
  hasAivDiscrepancy: boolean;
  hasArvDiscrepancy: boolean;
  isHybridDesktopComparison: boolean;
  renovationBudgetWarningMessage?: string;
  opinionOfMarketValueRangeWarningMessage?: string;
  opinionOfAsIsValueRangeWarningMessage?: string;
  opinionOfAfterRepairValueRangeWarningMessage?: string;
  showIndicatedCostWarningGeneral: boolean;
  showIndicatedCostWarningAiv: boolean;
  showIndicatedCostWarningArv: boolean;
  shouldShowFeasibilityCommentary: boolean;
  roiDisplay: string;
  roiDisplayColor: string;
  totalCostToCureError?: ValidationError;
  totalCostToCureCommentaryError?: ValidationError;
  opinionOfMarketValueError?: ValidationError;
  opinionOfAfterRepairValueError?: ValidationError;
  totalRenovationBudgetError?: ValidationError;
  feasibilityCommentaryError?: ValidationError;
  aivDiscrepancyCommentaryError?: ValidationError;
  arvDiscrepancyCommentaryError?: ValidationError;
  renderChartOrSummary: (compType?: 'aiv' | 'arv') => ReactNode;
  handleChange: () => void;
  setSectionRef: (el: HTMLDivElement | null) => void;
}) => (
  <Flex
    ref={setSectionRef}
    backgroundColor={active ? 'surface3' : 'surface1'}
    borderRadius="5px"
    padding="12px"
    flexDirection="column"
    gap={4}
  >
    <Typography variant="h1">Opinions of Value</Typography>
    {!isArvOrder && includeTotalCostToCure && (
      <Flex flexDirection="column">
        <Flex flex={1} justifyContent="space-between" alignItems="center">
          <Flex alignItems="center" gap="4px">
            <Typography variant="h3" color="textSecondary">
              Total Cost to Cure
              <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                {!isMockOrder && '*'}
              </span>
            </Typography>
            <InfoIcon tooltipContent="Major items only (i.e. soundness, security, livability). No cosmetic or minor deferred maintenance." />
          </Flex>
          <Controller
            name="totalCostToCure"
            control={form.control}
            render={({ field, fieldState }) => (
              <RequiredFieldAnchor
                fieldName="totalCostToCure"
                isFieldComplete={field.value != null}
              >
                <SimpleDollarInput
                  value={field.value ? field.value.toString(true, true) : ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value
                      ? AloftPrice.fromDollarLike(e.target.value)
                      : undefined;
                    field.onChange(value);
                    handleChange();
                  }}
                  onBlur={field.onBlur}
                  required={!isMockOrder}
                  placeholder={
                    isMockOrder
                      ? 'Optional'
                      : 'Required. Please describe the major items (i.e. soundness, security, livability) that account for this cost to cure estimation. Do not include cosmetic or minor deferred maintenance.'
                  }
                  width="300px"
                  errorMessage={fieldState.isTouched ? totalCostToCureError?.message : undefined}
                  invalid={fieldState.invalid && fieldState.isTouched}
                />
              </RequiredFieldAnchor>
            )}
          />
        </Flex>
        <Controller
          name="totalCostToCureCommentary"
          control={form.control}
          render={({ fieldState }) => (
            <FormTextarea
              name="totalCostToCureCommentary"
              requiredFieldName="totalCostToCureCommentary"
              control={form.control}
              placeholder={`${!isMockOrder ? 'Required' : 'Optional'}. Please describe the major items (i.e. soundness, security, livability) that account for this cost to cure estimation. Do not include cosmetic or minor deferred maintenance.`}
              isRequired={!isMockOrder}
              labelWidth="250px"
              inputWidth="500px"
              height="200px"
              labelColor="textSecondary"
              size="sm"
              resize="vertical"
              customErrorMessage={
                fieldState.isTouched ? totalCostToCureCommentaryError?.message : undefined
              }
              column
            />
          )}
        />
      </Flex>
    )}
    {isArvOrder && (
      <Flex flexDirection="column" gap={2}>
        <Flex flex={1} justifyContent="space-between" alignItems="center">
          <Block>
            <Typography variant="h3" color="textSecondary">
              Renovation Budget
              <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                {!isMockOrder && '*'}
              </span>
            </Typography>
          </Block>
          <Controller
            name="totalRenovationBudget"
            control={form.control}
            render={({ field, fieldState }) => (
              <RequiredFieldAnchor
                fieldName="totalRenovationBudget"
                isFieldComplete={field.value != null}
              >
                <SimpleDollarInput
                  value={field.value ? field.value.toString(true, true) : ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value
                      ? AloftPrice.fromDollarLike(e.target.value)
                      : undefined;
                    field.onChange(value);
                    handleChange();
                  }}
                  onBlur={field.onBlur}
                  required={!isMockOrder}
                  placeholder={isMockOrder ? 'Optional' : 'Required'}
                  width="300px"
                  errorMessage={
                    fieldState.isTouched ? totalRenovationBudgetError?.message : undefined
                  }
                  invalid={fieldState.invalid && fieldState.isTouched}
                  warning={!!renovationBudgetWarningMessage}
                />
              </RequiredFieldAnchor>
            )}
          />
        </Flex>
        {renovationBudgetWarningMessage && (
          <Flex alignItems="center" gap={2}>
            <Icon icon="error-warning" color="alertWarning" />
            <Typography variant="h5" color="textPrimary">
              {renovationBudgetWarningMessage}
            </Typography>
          </Flex>
        )}
      </Flex>
    )}
    {!isArvOrder && (
      <Flex flexDirection="column" gap={2}>
        <Flex flex={1} justifyContent="space-between" alignItems="center">
          <Block>
            <Typography variant="h3" color="textSecondary">
              Opinion of Market Value
              <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                {!isMockOrder && '*'}
              </span>
            </Typography>
            {priorDesktopAiv && (
              <Typography variant="body2" color="textSecondary">
                Previous Desktop Value: {formatValue(priorDesktopAiv)}
              </Typography>
            )}
          </Block>
          <Controller
            name="opinionOfMarketValue"
            control={form.control}
            render={({ field }) => (
              <RequiredFieldAnchor
                fieldName="opinionOfMarketValue"
                isFieldComplete={field.value != null}
              >
                <SimpleDollarInput
                  value={field.value ? field.value.toString(true, true) : ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value
                      ? AloftPrice.fromDollarLike(e.target.value)
                      : undefined;
                    field.onChange(value);
                    handleChange();
                  }}
                  onBlur={field.onBlur}
                  required={!isMockOrder}
                  placeholder={isMockOrder ? 'Optional' : 'Required'}
                  width="300px"
                  errorMessage={
                    opinionOfMarketValueError?.type === ValidationErrorType.INVALID
                      ? opinionOfMarketValueError?.message
                      : ''
                  }
                  warning={!!opinionOfMarketValueRangeWarningMessage}
                />
              </RequiredFieldAnchor>
            )}
          />
        </Flex>
        {opinionOfMarketValueRangeWarningMessage && (
          <Flex alignItems="center" gap={2}>
            <Icon icon="error-warning" color="alertWarning" />
            <Typography variant="h5" color="textPrimary">
              {opinionOfMarketValueRangeWarningMessage}
            </Typography>
          </Flex>
        )}
      </Flex>
    )}
    {!isArvOrder && showIndicatedCostWarningGeneral && (
      <Flex alignItems="flex-start" gap={2}>
        <Icon icon="error-warning" color="alertWarning" size="md" />
        <Typography variant="h5" color="textPrimary" __dangerDoNotUseSx={{ lineHeight: '100%' }}>
          Indicated Cost Value beyond 25% of Median Comp Adjusted Sale Price. Align the two
          approaches, or explain the difference between the Cost and Sales Comparison approaches.
        </Typography>
      </Flex>
    )}
    {!isArvOrder && renderChartOrSummary()}
    {!isArvOrder && hasAivDiscrepancy && (
      <Flex flexDirection="column" gap={3}>
        <Flex alignItems="center" gap={2}>
          <DiscrepancyWarningIcon />
          <Typography variant="h5" color="textPrimary" __dangerDoNotUseSx={{ lineHeight: '100%' }}>
            Your Opinion of Market Value differs between this current Hybrid appraisal and the
            previous Desktop Appraisal. Explain the difference in the Value Discrepancy Commentary
            below.
          </Typography>
        </Flex>
        <Controller
          name="aivDiscrepancyCommentary"
          control={form.control}
          render={({ fieldState }) => (
            <FormTextarea
              name="aivDiscrepancyCommentary"
              requiredFieldName="aivDiscrepancyCommentary"
              control={form.control}
              label="Value Discrepancy Commentary"
              placeholder={
                isMockOrder
                  ? 'Optional'
                  : 'Please acknowledge the prior Desktop service and evaluate the impact of the interior inspection on the previous value conclusion. Clearly document any material discrepancies found during the site visit and their subsequent effect on the appraised value.'
              }
              isRequired={!isMockOrder}
              labelWidth="250px"
              inputWidth="500px"
              labelColor="textSecondary"
              labelVariant="h3"
              size="sm"
              resize="vertical"
              column
              customErrorMessage={
                fieldState.isTouched ? aivDiscrepancyCommentaryError?.message : undefined
              }
            />
          )}
        />
      </Flex>
    )}
    {isArvOrder && (
      <>
        <Flex flexDirection="column" gap={2}>
          <Flex flex={1} justifyContent="space-between" alignItems="center">
            <Block>
              <Flex alignItems="center" gap={2}>
                <Typography variant="h3" color="textSecondary">
                  Opinion of As-Is Market Value
                  <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                    {!isMockOrder && '*'}
                  </span>
                </Typography>
                <Typography
                  variant="body1"
                  color="aivText"
                  __dangerDoNotUseSx={{
                    textTransform: 'uppercase',
                    backgroundColor: colors.aiv,
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 700,
                    lineHeight: '17px',
                  }}
                >
                  AIV
                </Typography>
              </Flex>
              {isHybridDesktopComparison && priorDesktopAiv && (
                <Typography variant="body2" color="textSecondary">
                  Previous Desktop AIV: {formatValue(priorDesktopAiv)}
                </Typography>
              )}
            </Block>
            <Controller
              name="opinionOfMarketValue"
              control={form.control}
              render={({ field }) => (
                <RequiredFieldAnchor
                  fieldName="opinionOfMarketValue"
                  isFieldComplete={field.value != null}
                >
                  <SimpleDollarInput
                    value={field.value ? field.value.toString(true, true) : ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const trimmedValue = e.target.value.trim();
                      const value = trimmedValue
                        ? AloftPrice.fromDollarLike(trimmedValue)
                        : undefined;

                      field.onChange(value);
                      handleChange();
                    }}
                    onBlur={field.onBlur}
                    required
                    placeholder="Required"
                    width="300px"
                    errorMessage={
                      opinionOfMarketValueError?.type === ValidationErrorType.INVALID
                        ? opinionOfMarketValueError?.message
                        : ''
                    }
                    warning={!!opinionOfAsIsValueRangeWarningMessage}
                  />
                </RequiredFieldAnchor>
              )}
            />
          </Flex>
          {opinionOfAsIsValueRangeWarningMessage && (
            <Flex alignItems="center" gap={2}>
              <Icon icon="error-warning" color="alertWarning" />
              <Typography variant="h5" color="textPrimary">
                {opinionOfAsIsValueRangeWarningMessage}
              </Typography>
            </Flex>
          )}
        </Flex>
        {renderChartOrSummary('aiv')}
        {showIndicatedCostWarningAiv && (
          <Flex alignItems="flex-start" gap={2}>
            <Icon icon="error-warning" color="alertWarning" size="md" />
            <Typography
              variant="h5"
              color="textPrimary"
              __dangerDoNotUseSx={{ lineHeight: '100%' }}
            >
              <Typography
                variant="body1"
                color="aivText"
                __dangerDoNotUseSx={{
                  display: 'inline',
                  backgroundColor: colors.aiv,
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 700,
                  marginRight: '6px',
                  textTransform: 'uppercase',
                }}
              >
                AIV
              </Typography>
              Indicated Cost Value beyond 25% of Median Comp Adjusted Sale Price. Align the two
              approaches, or explain the difference between the Cost and Sales Comparison
              approaches.
            </Typography>
          </Flex>
        )}
        {hasAivDiscrepancy && (
          <Flex flexDirection="column" gap={3}>
            <Flex alignItems="center" gap={2}>
              <DiscrepancyWarningIcon />
              <Typography
                variant="h5"
                color="textPrimary"
                __dangerDoNotUseSx={{ lineHeight: '100%' }}
              >
                Previous Desktop AIV was {formatValue(priorDesktopAiv)} and Hybrid AIV is{' '}
                {formatValue(watchedOpinionOfMarketValuePrice)}. This is a{' '}
                {discrepancyDifference(aivDelta)} {discrepancyDirection(aivDelta)}. Provide detailed
                commentary explaining the difference.
              </Typography>
            </Flex>
            <Controller
              name="aivDiscrepancyCommentary"
              control={form.control}
              render={({ fieldState }) => (
                <FormTextarea
                  name="aivDiscrepancyCommentary"
                  requiredFieldName="aivDiscrepancyCommentary"
                  control={form.control}
                  label="AIV Discrepancy Commentary"
                  placeholder={
                    isMockOrder
                      ? 'Optional'
                      : 'Please acknowledge the prior Desktop service and evaluate the impact of the interior inspection on the previous value conclusion. Clearly document any material discrepancies found during the site visit and their subsequent effect on the appraised value.'
                  }
                  isRequired={!isMockOrder}
                  labelWidth="250px"
                  inputWidth="500px"
                  labelColor="textSecondary"
                  labelVariant="h3"
                  size="sm"
                  resize="vertical"
                  column
                  customErrorMessage={
                    fieldState.isTouched ? aivDiscrepancyCommentaryError?.message : undefined
                  }
                />
              )}
            />
          </Flex>
        )}
        <Flex flexDirection="column" gap={2}>
          <Flex flex={1} justifyContent="space-between" alignItems="center">
            <Block>
              <Flex alignItems="center" gap={2}>
                <Typography variant="h3" color="textSecondary">
                  Opinion of After-Repair Market Value
                  <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                    {!isMockOrder && '*'}
                  </span>
                </Typography>
                <Typography
                  variant="body1"
                  color="textOffset"
                  __dangerDoNotUseSx={{
                    textTransform: 'uppercase',
                    backgroundColor: colors.arv,
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 700,
                    lineHeight: '17px',
                  }}
                >
                  ARV
                </Typography>
              </Flex>
              {isHybridDesktopComparison && priorDesktopArv && (
                <Typography variant="body2" color="textSecondary">
                  Previous Desktop ARV: {formatValue(priorDesktopArv)}
                </Typography>
              )}
            </Block>
            <Controller
              name="opinionOfAfterRepairValue"
              control={form.control}
              render={({ field }) => (
                <RequiredFieldAnchor
                  fieldName="opinionOfAfterRepairValue"
                  isFieldComplete={field.value != null}
                >
                  <SimpleDollarInput
                    value={field.value ? field.value.toString(true, true) : ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const trimmedValue = e.target.value.trim();
                      const value = trimmedValue
                        ? AloftPrice.fromDollarLike(trimmedValue)
                        : undefined;
                      field.onChange(value);
                      handleChange();
                    }}
                    onBlur={field.onBlur}
                    required
                    placeholder="Required"
                    width="300px"
                    errorMessage={
                      opinionOfAfterRepairValueError?.type === ValidationErrorType.INVALID
                        ? opinionOfAfterRepairValueError?.message
                        : ''
                    }
                    warning={!!opinionOfAfterRepairValueRangeWarningMessage}
                  />
                </RequiredFieldAnchor>
              )}
            />
          </Flex>
          {opinionOfAfterRepairValueRangeWarningMessage && (
            <Flex alignItems="center" gap={2}>
              <Icon icon="error-warning" color="alertWarning" />
              <Typography variant="h5" color="textPrimary">
                {opinionOfAfterRepairValueRangeWarningMessage}
              </Typography>
            </Flex>
          )}
        </Flex>
        {renderChartOrSummary('arv')}
        {showIndicatedCostWarningArv && (
          <Flex alignItems="flex-start" gap={2}>
            <Icon icon="error-warning" color="alertWarning" size="md" />
            <Typography
              variant="h5"
              color="textPrimary"
              __dangerDoNotUseSx={{ lineHeight: '100%' }}
            >
              <Typography
                variant="body1"
                color="arvText"
                __dangerDoNotUseSx={{
                  display: 'inline',
                  backgroundColor: colors.arv,
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 700,
                  marginRight: '6px',
                  textTransform: 'uppercase',
                }}
              >
                ARV
              </Typography>
              Indicated Cost Value beyond 25% of Median Comp Adjusted Sale Price. Align the two
              approaches, or explain the difference between the Cost and Sales Comparison
              approaches.
            </Typography>
          </Flex>
        )}
        {hasArvDiscrepancy && (
          <Flex flexDirection="column" gap={3}>
            <Flex alignItems="center" gap={2}>
              <DiscrepancyWarningIcon />
              <Typography
                variant="h5"
                color="textPrimary"
                __dangerDoNotUseSx={{ lineHeight: '100%' }}
              >
                Previous Desktop ARV was {formatValue(priorDesktopArv)} and Hybrid ARV is{' '}
                {formatValue(watchedOpinionOfAfterRepairValuePrice)}. This is a{' '}
                {discrepancyDifference(arvDelta)} {discrepancyDirection(arvDelta)}. Provide detailed
                commentary explaining the difference.
              </Typography>
            </Flex>
            <Controller
              name="arvDiscrepancyCommentary"
              control={form.control}
              render={({ fieldState }) => (
                <FormTextarea
                  name="arvDiscrepancyCommentary"
                  requiredFieldName="arvDiscrepancyCommentary"
                  control={form.control}
                  label="ARV Discrepancy Commentary"
                  placeholder={
                    isMockOrder
                      ? 'Optional'
                      : 'Please acknowledge the prior Desktop service and evaluate the impact of the interior inspection on the previous value conclusion. Clearly document any material discrepancies found during the site visit and their subsequent effect on the appraised value.'
                  }
                  isRequired={!isMockOrder}
                  labelWidth="250px"
                  inputWidth="500px"
                  labelColor="textSecondary"
                  labelVariant="h3"
                  size="sm"
                  resize="vertical"
                  column
                  customErrorMessage={
                    fieldState.isTouched ? arvDiscrepancyCommentaryError?.message : undefined
                  }
                />
              )}
            />
          </Flex>
        )}
        <Flex flex={1} justifyContent="space-between" alignItems="center">
          <Block>
            <Typography variant="h3" color="textLight">
              Estimated Return on Investment
            </Typography>
            <Typography variant="body2" color="textLight">
              {'\u{2248} After-Repair Value - (Renovation Budget + As-Is Value)'}
            </Typography>
          </Block>
          <Flex flexDirection="column" alignItems="flex-end" justifyContent="center">
            <Typography variant="body1" __dangerDoNotUseSx={{ color: roiDisplayColor }}>
              {roiDisplay}
            </Typography>
            <Typography variant="body2" color="textLight">
              {`\u{2248} ${formatValue(watchedOpinionOfAfterRepairValuePrice)} - (${formatValue(watchedTotalRenovationBudgetPrice)} + ${formatValue(watchedOpinionOfMarketValuePrice)})`}
            </Typography>
          </Flex>
        </Flex>
        {shouldShowFeasibilityCommentary && (
          <Flex flex={1} flexDirection="column">
            <Controller
              name="feasibilityCommentary"
              control={form.control}
              render={({ fieldState }) => (
                <FormTextarea
                  name="feasibilityCommentary"
                  requiredFieldName="feasibilityCommentary"
                  control={form.control}
                  label="Feasibility Commentary"
                  placeholder={
                    isMockOrder
                      ? 'Optional'
                      : 'Required. Provide a narrative explanation for why you believe the ROI on this property is negative. Reminder that the AIV reflects current conditions, while the ARV reflects post-repair conditions.'
                  }
                  isRequired={!isMockOrder}
                  labelWidth="250px"
                  inputWidth="500px"
                  height="200px"
                  labelColor="textSecondary"
                  labelVariant="h3"
                  size="sm"
                  resize="vertical"
                  customErrorMessage={
                    fieldState.isTouched ? feasibilityCommentaryError?.message : undefined
                  }
                  column
                />
              )}
            />
          </Flex>
        )}
      </>
    )}
  </Flex>
);
